// Stimulate (ba-web, with ba-stim's FEM field): the coil current's intensity, how the field reaches the
// viewport, and the coil as ba-stim's glyph mesh. The run (stim-sim-v0) is one static FEM field at 1 MA/s; at
// fixed geometry the field is linear in the coil current's slew rate, so intensity is honest arithmetic on |E|
// (a × E at a MA/s), never a motor threshold. The field reaches the picture through the engine's generic
// vertex layer (#2, ba-engine; there is no stim-specific engine reader, ba-stim and ba-app 20:53 UTC): the
// app pushes |E| when the viewport has `setVertexData`, and on one without it the panel's numbers are the
// run's and the picture waits. The coil is the dipole glyph mesh of ba-stim's browser module
// (pipelines/stim/browser, STIM-002, #669), posed by `setPose`; a coil drag will take the module's sampler and
// projector and push the pose and the field together, without a timestamp.

import { create } from 'zustand';
import type { StimSim } from '../data/sims/stim.ts';
import type { StimElectrode, StimHeader } from '../data/sims/types.ts';
import type { Command, Viewport } from '../viewport/api.ts';
import { CORTEX_SHADE, hexRgb, RAMPS, sampleStops, type Stops } from '../ui/scales.ts';
import { createCoilProjector, createDipoleMesh, type CoilProjector } from '../../../../pipelines/stim/browser/coil.mjs';
import { columnMajorPose, createFieldSampler, type FieldSampler } from '../../../../pipelines/stim/browser/field.mjs';
import rule from '../../../../pipelines/stim/browser/ernie-c3-rule.json';
import { COIL_LOOK, createCasingMesh } from './coilCasing.ts';

export const FIELD_LAYER = 'stim:field';
export const COIL_MESH = 'stim:coil';
/** The coil's casing, ba-look's translucent figure-eight shell (features/coilCasing.ts), posed with the dipoles. */
export const CASING_MESH = 'stim:casing';
/** The scene's fixed display range, V/m: the live grid's [0, 2] at 1 MA/s (the static C3 snapshot's own is [0, 1.8]).
 *  The lead fixed the whole scene on the grid's, so the bar doesn't rescale when the drag starts. */
export const FIELD_RANGE: [number, number] = [0, 2];

// The field's look (ba-look's direction through the lead, 10-03): the legend is the lookup table. The cortex is coloured by |E|
// in the legend's own ramp from 15 % up (its near-black bottom skipped), the strength rising from 5 % to 30 % of the range
// (the engine's `rise` is linear where the direction asks for a smoothstep; the engine owner has the note), the tissue's albedo
// fully replaced at full strength, no wash and no floor under the rise. Opacity, not darkness, says "no field"; the shading
// stays the surface's own. The ranges are fixed, never autoscaled: a tDCS run's own (0–0.6 V/m), TMS the grid's 0–2.
/** Where the legend's ramp starts on the cortex: t = 0 draws LUT(0.15). */
export const FIELD_LUT_FROM = 0.15;
/** The strength's rise over t: nothing under 5 % of the range, full from 30 % (`[floor, span]`, the engine's linear ramp). */
export const FIELD_RISE: [number, number] = [0.05, 0.25];
/** The field's colour stops: the drive ramp sampled from `from` to 1 at `n` even stops, so the engine's LUT and the legend agree. */
export function fieldStops(from = FIELD_LUT_FROM, n = 9): Stops {
  const out: Stops = [];
  const rgb: [number, number, number] = [0, 0, 0];
  for (let k = 0; k < n; k++) {
    const t = k / (n - 1);
    sampleStops(RAMPS.drive, from + (1 - from) * t, rgb);
    out.push([t, `#${rgb.map((v) => v.toString(16).padStart(2, '0')).join('')}`]);
  }
  return out;
}
/** The strength at t over the range: clamp((t − floor) / span). */
export const fieldStrength = (t: number, rise: [number, number] = FIELD_RISE): number => Math.min(1, Math.max(0, (t - rise[0]) / rise[1]));
export interface FieldLook {
  colormap: { stops: Stops };
  range: [number, number];
  blend: 'mix';
  albedo: number;
  emission: number;
  rise: [number, number];
  weakMix: number;
  albedoFloor: number;
}
/** The live layer's look over a range. */
export const fieldLook = (range: [number, number]): FieldLook => ({ colormap: { stops: fieldStops() }, range, blend: 'mix', albedo: 1, emission: 0, rise: FIELD_RISE, weakMix: 0, albedoFloor: 0 });
/** The legend bar as the cortex takes the field: the ramp's colour at each t with the strength as its alpha, over the cortex's
 *  shade (the CSS stacks the gradient on the swatch). */
export function fieldBarCss(n = 25, tissue = CORTEX_SHADE): string {
  const stops = fieldStops();
  const rgb: [number, number, number] = [0, 0, 0];
  const parts: string[] = [];
  for (let k = 0; k < n; k++) {
    const t = k / (n - 1);
    sampleStops(stops, t, rgb);
    parts.push(`rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${fieldStrength(t).toFixed(3)}) ${(t * 100).toFixed(1)}%`);
  }
  return `linear-gradient(to right, ${parts.join(', ')}), ${tissue}`;
}
/** A colour at `keep` of its saturation: each channel pulled toward its own luminance (ba-look's 60 % for the pads). */
export function desaturate(hex: string, keep: number): string {
  const [r, g, b] = hexRgb(hex);
  const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const c = (v: number) => Math.round(Math.min(255, Math.max(0, y + (v - y) * keep))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}
/** The current a tDCS montage opens at, mA: a common dose that puts the first export's peak near the top of its fixed
 *  0–0.6 V/m scale (0.572 V/m); the control's reference stays the FEM's own 1 mA, which the field scales from. */
export const DEFAULT_SHOWN_CURRENT_MA = 2;
/** Ernie's live basis' patch: ±10 mm in the seed coil's tangent axes around C3 (STIM-001), the coil keeping its orientation.
 *  The default where a bundle names no rectangle. */
export const PATCH_MM = 10;
/** A basis' patch, mm, in the seed coil's tangent axes: `[[x0, x1], [y0, y1]]`, the rectangle its grid covers and the live
 *  field is validated on (the contract's `basis.valid_rectangle_mm`). Ernie's nine poses span ±10 mm (STIM-001), IXI165's four
 *  corners ±5 mm (STIM-008). The pad draws it, the clamp and the arrow keys stop at its edge, and nothing is sampled outside. */
export type Patch = [[number, number], [number, number]];
export const DEFAULT_PATCH: Patch = [
  [-PATCH_MM, PATCH_MM],
  [-PATCH_MM, PATCH_MM],
];
const isSpan = (p: unknown): p is [number, number] => Array.isArray(p) && p.length === 2 && p.every((v) => typeof v === 'number' && Number.isFinite(v)) && (p[1] as number) > (p[0] as number);
export function patchOf(h: Pick<StimHeader, 'basis'> | null | undefined): Patch {
  const r = h?.basis?.valid_rectangle_mm;
  return Array.isArray(r) && isSpan(r[0]) && isSpan(r[1])
    ? [
        [r[0][0], r[0][1]],
        [r[1][0], r[1][1]],
      ]
    : DEFAULT_PATCH;
}
/** The patch in words: "±10 mm" when it's the same both ways about C3, else each axis's span. */
export function patchText(p: Patch): string {
  const sym = (s: [number, number]) => (s[0] === -s[1] ? s[1] : null);
  const x = sym(p[0]);
  const y = sym(p[1]);
  if (x !== null && x === y) return `±${x} mm`;
  const span = (s: [number, number]) => `${s[0] < 0 ? '−' : '+'}${Math.abs(s[0])} to ${s[1] < 0 ? '−' : '+'}${Math.abs(s[1])} mm`;
  return `${span(p[0])} across, ${span(p[1])} along`;
}

/** The bar's top tick, and whether the peak shown tops the bar: the range stays the grid's fixed [0, 2] V/m (colour
 *  shows intensity changes), so above it the colour clamps to the top; the tick then reads "2+" and the Field note
 *  says so (lead, 2026-10-02 22:44 UTC). */
export function barTop(peak: number | null, hi: number = FIELD_RANGE[1]): { label: string; saturated: boolean } {
  const saturated = peak !== null && Number.isFinite(peak) && peak > hi + 1e-9;
  return { label: saturated ? `${hi}+` : `${hi}`, saturated };
}
/** A coil offset from C3 in that patch, mm: across (x) and along (y) the scalp's tangent plane at the seed. */
export type Offset = [number, number];
const clampMm = (v: number, [lo, hi]: [number, number]): number => (Number.isFinite(v) ? Number(Math.min(hi, Math.max(lo, v)).toFixed(1)) : 0);
/** The offset inside the patch, to a tenth of a millimetre; a non-number goes to C3. */
export const clampOffset = (o: Offset, patch: Patch = DEFAULT_PATCH): Offset => [clampMm(o[0], patch[0]), clampMm(o[1], patch[1])];
/** Whether the offset sits on the patch's edge on each axis, where the drag is clamped and the panel shows the limit. */
export const atLimit = (o: Offset, patch: Patch = DEFAULT_PATCH): { x: boolean; y: boolean } => ({
  x: o[0] <= patch[0][0] || o[0] >= patch[0][1],
  y: o[1] <= patch[1][0] || o[1] >= patch[1][1],
});
/** The intensity control: the coil current's slew rate, MA/s, around the run's own 1 MA/s. */
export const INTENSITY = { min: 0.25, max: 2, step: 0.05, unit: 'MA/s' } as const;

// tDCS (stim-sim-v0's additive kind, docs/contracts/stim-sim-v0.md): one fixed montage's static field on the same cortex,
// linear in the current at fixed pads and conductivities. The pads are the FEM's own electrode boundaries, drawn where the
// run puts them in the head's RAS+ mm; the scalp the run's own surface; no pad moves (ba-stim's STIM-007 export).

export const isTdcs = (h: Pick<StimHeader, 'kind'>): boolean => h.kind === 'tdcs';

/** The current control, mA: the header's `display.current_control` (0–2 mA by 0.1 around the FEM's 1 mA in the first
 *  export), else that. The multiplier on the signed field is current / reference; |E| takes its absolute value. */
export interface CurrentControl {
  min: number;
  max: number;
  step: number;
  reference: number;
  unit: 'mA';
}
export const DEFAULT_CURRENT: CurrentControl = { min: 0, max: 2, step: 0.1, reference: 1, unit: 'mA' };
const mA = (a: number | undefined, fallback: number): number => (typeof a === 'number' && Number.isFinite(a) && a >= 0 ? Number((a * 1000).toFixed(4)) : fallback);
export function currentControl(h: Pick<StimHeader, 'display'>): CurrentControl {
  const c = h.display?.current_control;
  const reference = mA(c?.reference_A, DEFAULT_CURRENT.reference) || DEFAULT_CURRENT.reference;
  const step = mA(c?.step_A, DEFAULT_CURRENT.step) || DEFAULT_CURRENT.step;
  const min = mA(c?.range_A?.[0], DEFAULT_CURRENT.min);
  const max = Math.max(min + step, mA(c?.range_A?.[1], DEFAULT_CURRENT.max));
  return { min, max, step, reference, unit: 'mA' };
}
export const clampCurrent = (v: number, c: CurrentControl = DEFAULT_CURRENT): number => {
  const n = Number.isFinite(v) ? Math.min(c.max, Math.max(c.min, v)) : c.reference;
  return Number((Math.round(n / c.step) * c.step).toFixed(4));
};
/** |E|'s multiplier at a current: |I / I_ref|, the contract's rule for a fixed montage. */
export const currentScale = (v: number, c: CurrentControl = DEFAULT_CURRENT): number => Math.abs(v / c.reference);
/** The scene's colour range for a run, V/m: a tDCS run's own fixed range (0–0.6 in the first export; the panel reads it
 *  from the run, never rescaled by the current), TMS the grid's [0, 2]. */
export function fieldRange(h: Pick<StimHeader, 'kind' | 'display'>): [number, number] {
  const r = h.display?.range;
  if (isTdcs(h) && r && Number.isFinite(r[0]) && Number.isFinite(r[1]) && r[1] > r[0]) return [Number(r[0].toFixed(3)), Number(r[1].toFixed(3))];
  return FIELD_RANGE;
}

export const SCALP_MESH = 'stim:scalp';
export const padMeshId = (electrode: string): string => `stim:pad:${electrode}`;
/** The pads' colours, the anode (current into the head) warm and the cathode cool, as design's diverging pressure scale
 *  ends (`RAMPS.pressure`) and ba-stim's preview sheet paint them; the scalp a ghost in the engine's slate with the ghost
 *  fresnel, no depth write, its per-face opacity half the look's 0.12 since a closed shell's two faces compose. */
export const MONTAGE_LOOK = {
  // At 60 % of their saturation and matte, so the pads' red stops competing with the ramp's orange (ba-look, 10-03).
  anode: desaturate(RAMPS.pressure[4]![1], 0.6),
  cathode: desaturate(RAMPS.pressure[0]![1], 0.6),
  scalp: { color: '#9AA6B8', face_opacity: 0.06, fresnel: { color: '#9AA6B8', power: 3, strength: 0.35 } },
} as const;

/** What the viewport drew of a montage, once asked: the pads by electrode id, and the scalp. */
export interface MontageDrawn {
  pads: string[];
  scalp: boolean;
}

/** One pad's mesh out of the run's electrode arrays: its span's vertices (and normals), its triangles re-based from the
 *  array's global zero-based indices to its own; copied, so the engine gets standalone buffers. */
export function padMesh(e: StimElectrode, vertices: Float32Array, normals: Float32Array | null, triangles: Uint32Array): { positions: Float32Array; normals: Float32Array | null; indices: Uint32Array } {
  const v0 = e.vertex_offset * 3;
  const v1 = (e.vertex_offset + e.vertex_count) * 3;
  if (v1 > vertices.length || (normals && v1 > normals.length)) throw new Error(`electrode ${e.id}: its vertex span runs past the array`);
  const t0 = e.triangle_offset * 3;
  const t1 = (e.triangle_offset + e.triangle_count) * 3;
  if (t1 > triangles.length) throw new Error(`electrode ${e.id}: its triangle span runs past the array`);
  const indices = new Uint32Array(t1 - t0);
  for (let i = t0; i < t1; i++) {
    const k = triangles[i]! - e.vertex_offset;
    if (!(k >= 0 && k < e.vertex_count)) throw new Error(`electrode ${e.id}: triangle index ${triangles[i]} is outside its span`);
    indices[i - t0] = k;
  }
  return { positions: vertices.slice(v0, v1), normals: normals ? normals.slice(v0, v1) : null, indices };
}

/** Area-weighted vertex normals of a triangle mesh, for a surface the run gives without them (the scalp). */
export function vertexNormals(positions: Float32Array, indices: Uint32Array): Float32Array {
  const n = new Float32Array(positions.length);
  for (let i = 0; i + 2 < indices.length; i += 3) {
    const a = indices[i]! * 3;
    const b = indices[i + 1]! * 3;
    const c = indices[i + 2]! * 3;
    const ux = positions[b]! - positions[a]!;
    const uy = positions[b + 1]! - positions[a + 1]!;
    const uz = positions[b + 2]! - positions[a + 2]!;
    const vx = positions[c]! - positions[a]!;
    const vy = positions[c + 1]! - positions[a + 1]!;
    const vz = positions[c + 2]! - positions[a + 2]!;
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    for (const k of [a, b, c]) {
      n[k] = n[k]! + nx;
      n[k + 1] = n[k + 1]! + ny;
      n[k + 2] = n[k + 2]! + nz;
    }
  }
  for (let k = 0; k < n.length; k += 3) {
    const l = Math.hypot(n[k]!, n[k + 1]!, n[k + 2]!) || 1;
    n[k] = n[k]! / l;
    n[k + 1] = n[k + 1]! / l;
    n[k + 2] = n[k + 2]! / l;
  }
  return n;
}

/** The montage in the viewport, when it takes meshes: each pad as the FEM's own electrode boundary (derived, opaque, the
 *  anode warm and the cathode cool), and the run's scalp as a ghost so the pads sit on a head, unless the caller leaves it
 *  off (`scalp: false`): IXI 165's pack carries a defaced head surface of its own, and the CHARM run's own scalp carries the
 *  participant's face (the lead, 10-03; ba-stim's defaced `-v2` runs will replace it). What drew comes back. */
export async function showMontage(vp: Pick<Viewport, 'send'>, sim: Pick<StimSim, 'header' | 'files'>, opts: { scalp?: boolean } = {}): Promise<MontageDrawn> {
  const h = sim.header;
  const a = h.arrays;
  const pads: string[] = [];
  let scalp = false;
  if (h.electrodes?.length && a.electrode_vertices && a.electrode_triangles) {
    const [v, t, n] = await Promise.all([sim.files.array(a.electrode_vertices), sim.files.array(a.electrode_triangles), a.electrode_normals ? sim.files.array(a.electrode_normals) : null]);
    if (!(v instanceof Float32Array) || !(t instanceof Uint32Array) || (n !== null && !(n instanceof Float32Array))) throw new Error('the electrode arrays aren’t f32 vertices and normals and u32 triangles');
    for (const e of h.electrodes) {
      try {
        const m = padMesh(e, v, n, t);
        const anode = (e.current_A ?? 0) >= 0;
        await vp.send({ cmd: 'mesh', id: padMeshId(e.id), positions: m.positions, ...(m.normals ? { normals: m.normals } : {}), indices: m.indices, color: anode ? MONTAGE_LOOK.anode : MONTAGE_LOOK.cathode, specular: 0, provenance: 'derived' } as unknown as Command);
        pads.push(e.id);
      } catch (err) {
        console.warn('[stim] pad', e.id, err);
      }
    }
  }
  if (opts.scalp !== false && a.scalp_vertices && a.scalp_triangles) {
    try {
      const [sv, st] = await Promise.all([sim.files.array(a.scalp_vertices), sim.files.array(a.scalp_triangles)]);
      if (!(sv instanceof Float32Array) || !(st instanceof Uint32Array)) throw new Error('the scalp arrays aren’t f32 vertices and u32 triangles');
      const look = MONTAGE_LOOK.scalp;
      await vp.send({ cmd: 'mesh', id: SCALP_MESH, positions: sv, normals: vertexNormals(sv, st), indices: st, color: look.color, opacity: look.face_opacity, specular: 0, depthWrite: false, fresnel: look.fresnel, provenance: 'derived' } as unknown as Command);
      scalp = true;
    } catch (err) {
      console.warn('[stim] scalp', err);
    }
  }
  return { pads, scalp };
}

/** Whether the viewport draws meshes at all (the coil, the pads, the scalp): the live methods came with `mesh`, so a viewport
 *  without `setPose` (the 2D preview) gets no mesh and the panel says so. */
export const drawsMeshes = (vp: Pick<LiveViewport, 'setPose'>): boolean => typeof vp.setPose === 'function';

/** The montage's meshes taken away (a switch back to TMS on the same head; a load clears them itself). */
export async function clearMontage(vp: Pick<Viewport, 'send'>, pads: string[]): Promise<void> {
  for (const id of [...pads.map(padMeshId), SCALP_MESH]) await vp.send({ cmd: 'mesh', id, remove: true } as unknown as Command).catch(() => undefined);
}

/** The coil's meshes taken away (a switch to tDCS on the same head). */
export async function clearCoil(vp: Pick<Viewport, 'send'>): Promise<void> {
  for (const id of [CASING_MESH, COIL_MESH]) await vp.send({ cmd: 'mesh', id, remove: true } as unknown as Command).catch(() => undefined);
}

export const clampIntensity = (k: number): number => {
  const n = Number.isFinite(k) ? Math.min(INTENSITY.max, Math.max(INTENSITY.min, k)) : 1;
  return Number((Math.round(n / INTENSITY.step) * INTENSITY.step).toFixed(2));
};

/** What the viewport draws of the coil: ba-look's casing (a viewport whose `mesh` takes a look, engine a6ad22cc), the
 *  2,712 dipoles (the Source model, or the only coil on an older viewport), or neither (no meshes at all). */
export interface CoilDrawn {
  casing: boolean;
  dipoles: boolean;
}

interface StimState {
  /** The coil current's slew rate, MA/s. */
  intensity: number;
  setIntensity(k: number): void;
  /** A tDCS montage's current, mA (the first export's control: 0–2 mA by 0.1, the FEM's own 1 mA). */
  current: number;
  setCurrent(mA: number, control?: CurrentControl): void;
  /** What the viewport drew of a tDCS montage, once it was asked. */
  montage: MontageDrawn | null;
  setMontage(m: MontageDrawn | null): void;
  /** The coil's offset from C3 in the live basis' patch, mm; [0, 0] is the saved C3 pose. Clamped to the patch given, else
   *  Ernie's ±10 mm (a link's offset arrives before the bundle is known; the panel clamps it again to the bundle's). */
  offset: Offset;
  setOffset(o: Offset, patch?: Patch): void;
  /** The Source model: the FEM's dipoles shown inside the casing (design's default is off). */
  sourceModel: boolean;
  setSourceModel(on: boolean): void;
  /** What the viewport drew of the coil, once it was asked. */
  coil: CoilDrawn | null;
  setCoil(c: CoilDrawn | null): void;
}

export const useStim = create<StimState>((set) => ({
  intensity: 1,
  setIntensity: (k) => set({ intensity: clampIntensity(k) }),
  current: DEFAULT_SHOWN_CURRENT_MA,
  setCurrent: (v, control) => set({ current: clampCurrent(v, control) }),
  montage: null,
  setMontage: (m) => set({ montage: m }),
  offset: [0, 0],
  setOffset: (o, patch) => set({ offset: clampOffset(o, patch) }),
  sourceModel: false,
  setSourceModel: (on) => set({ sourceModel: !!on }),
  coil: null,
  setCoil: (c) => set({ coil: c }),
}));
if (typeof window !== 'undefined') (window as unknown as { __stim?: typeof useStim }).__stim = useStim;

/** |E| at an intensity: the field at 1 MA/s times the slew rate in MA/s. */
export function scaleField(m: Float32Array, k: number, out = new Float32Array(m.length)): Float32Array {
  for (let i = 0; i < m.length; i++) out[i] = m[i]! * k;
  return out;
}

/** The proposed live API (#2, ba-engine), optional on today's viewport: pushes and poses are methods, like setTime. */
export interface LiveViewport extends Viewport {
  setVertexData?(id: string, values: Float32Array | Uint8Array, t?: number): void;
  setPose?(id: string, m16: Float32Array): void;
}

export type FieldPath = 'pushed' | 'none';

/** How the field reaches the picture: pushed through the vertex layer, or not yet on this viewport. */
export const fieldPath = (vp: Pick<LiveViewport, 'setVertexData'>): FieldPath => (typeof vp.setVertexData === 'function' ? 'pushed' : 'none');

/** Whether the viewport draws the run's cortex as the brain's HD cortex (one surface per hemisphere, as it draws
 *  IXI 165's own cortex, `ixi-165-cortex-hd`) rather than as the structures' own meshes (Ernie's head). The engine
 *  binds a layer to an HD cortex only as `surface: 'cortex'`, its data the left hemisphere's then the right's, which
 *  is the order a stim run's surfaces keep; a structure of the HD cortex named on its own "isn't drawn in this scene". */
export const drawnAsHdCortex = (brain: { pack: string } | null | undefined, header: { pack?: { id?: string } | null }): boolean => !!brain && !!header.pack?.id && brain.pack === header.pack.id;

/** The field at intensity k, V/m, pushed to the vertex layer on the run's own cortex surfaces (made once per run)
 *  over the scene's fixed range: the colours stay V/m while the values scale with the slew rate. */
export async function showField(vp: LiveViewport, sim: Pick<StimSim, 'surfaces' | 'magnitude'>, k: number, made: { layer: boolean }, hd = false, range: [number, number] = FIELD_RANGE): Promise<FieldPath> {
  const path = fieldPath(vp);
  if (path === 'pushed') {
    if (!made.layer) {
      const surface = hd ? 'cortex' : sim.surfaces.map((s) => s.structure);
      await vp.send({ cmd: 'vertexLayer', id: FIELD_LAYER, surface, space: 'own', kind: 'scalar', look: fieldLook(range) } as unknown as Command);
      made.layer = true;
    }
    vp.setVertexData!(FIELD_LAYER, scaleField(await sim.magnitude(), k));
  }
  return path;
}

/** The coil in the viewport, when it takes meshes, in the coil's own mm, posed into the head by `setPose` with the
 *  header's row-major pose made column-major by the module's `columnMajorPose`. By default ba-look's casing: a
 *  translucent figure-eight shell with the ghost state's fresnel edge, no specular and no depth write (design's
 *  `devices.tms_coil`, engine #754), so the device reads at a glance and the |E| hot spot under it keeps its contrast.
 *  The shell is sent at its per-face opacity (0.04; its caps and walls compose to the look's 0.08 through a wing). The
 *  Source model puts the FEM's 2,712 dipoles inside it (0.5 mm octahedra at 0.6) and thins the shell to a per-face 0.02. A
 *  viewport before a6ad22cc refuses the look's keys: then the dipoles alone, at 0.65 mm, as before. */
export async function showCoil(vp: LiveViewport, coilPositions: Float32Array, pose: number[][], sourceModel = false): Promise<CoilDrawn> {
  if (typeof vp.setPose !== 'function') return { casing: false, dipoles: false };
  const m16 = columnMajorPose(pose);
  const look = COIL_LOOK.casing;
  let casing = false;
  try {
    const shell = createCasingMesh();
    await vp.send({
      cmd: 'mesh',
      id: CASING_MESH,
      positions: shell.positions,
      normals: shell.normals,
      indices: shell.indices,
      color: look.color,
      opacity: sourceModel ? COIL_LOOK.source_model.face_opacity_while_on : look.face_opacity,
      specular: look.specular,
      depthWrite: look.depth_write,
      fresnel: { color: look.fresnel.color, power: look.fresnel.power, strength: look.fresnel.strength },
      provenance: 'artist-made', // the shell is drawn from the note's numbers, not the device's geometry
    } as unknown as Command);
    vp.setPose(CASING_MESH, m16);
    casing = true;
  } catch (err) {
    console.warn('[stim] casing', err);
  }
  const dipoles = sourceModel || !casing;
  if (dipoles) {
    const glyphs = createDipoleMesh(coilPositions, casing ? COIL_LOOK.source_model.glyph_radius_mm : 0.65);
    // Inside the casing (a viewport with the look): the dipoles at the source model's opacity, their positions the FEM's
    // (derived). Alone, on an older viewport: only the keys it takes.
    const inner = casing ? { opacity: COIL_LOOK.source_model.opacity, provenance: 'derived' } : {};
    await vp.send({ cmd: 'mesh', id: COIL_MESH, positions: glyphs.positions, normals: glyphs.normals, indices: glyphs.indices, color: COIL_LOOK.source_model.color, ...inner } as unknown as Command);
    vp.setPose(COIL_MESH, m16);
  } else {
    // `remove: true` takes the mesh away (a mesh without positions is refused).
    await vp.send({ cmd: 'mesh', id: COIL_MESH, remove: true } as unknown as Command).catch(() => undefined);
  }
  return { casing, dipoles };
}

/** Which cortex a vertex of the run's mesh is on, for the peak's wording. */
export const whereOf = (sim: Pick<StimSim, 'structureOf'>, vertex: number): string => {
  const structure = sim.structureOf(vertex);
  return structure?.includes('left') ? 'on the left cortex' : structure?.includes('right') ? 'on the right cortex' : 'on the cortex';
};

/** The nine-pose FEM basis opened for the drag: ba-stim's sampler and projector over the grid bundle's own arrays, with
 *  the caller-owned buffers they fill (the engine copies what it's sent). The signed vectors are ~33 MB, so the basis
 *  opens on the first touch of the pad or a link with an offset, not with the scene. */
export interface LiveBasis {
  field: FieldSampler;
  coil: CoilProjector;
  magnitude: Float32Array;
  pose: Float32Array;
  vertexCount: number;
  /** The bundle's patch: the drag is clamped to it before sampling. */
  patch: Patch;
}

export const ERNIE_SPACE = 'subject:simnibs:ernie';

/** The handle landmark (CP3, in the head's mm) the projector points the coil's handle at: the bundle's own
 *  (`basis.y_reference_mm`, IXI165's native CP3; ba-stim's STIM-008 export), else, for Ernie's head alone, the module's
 *  frozen Ernie rule (`ernie-c3-rule.json`); another head without its own can't pose the coil (null). */
export function handleLandmark(h: Pick<StimHeader, 'space' | 'basis'>): [number, number, number] | null {
  const own = h.basis?.y_reference_mm;
  if (Array.isArray(own) && own.length === 3 && own.every((v) => typeof v === 'number' && Number.isFinite(v))) return [own[0], own[1], own[2]];
  if (h.space === ERNIE_SPACE) return rule.y_reference_mm as [number, number, number];
  return null;
}

export async function openLive(sim: Pick<StimSim, 'header' | 'files'>): Promise<LiveBasis> {
  const a = sim.header.arrays;
  for (const k of ['e_field', 'scalp_vertices', 'scalp_triangles']) if (!a[k]) throw new Error(`the grid bundle has no arrays.${k}`);
  const yRef = handleLandmark(sim.header);
  if (!yRef) throw new Error('the basis names no handle landmark (basis.y_reference_mm) for this head');
  const [e, sv, st] = await Promise.all([sim.files.array(a.e_field!), sim.files.array(a.scalp_vertices!), sim.files.array(a.scalp_triangles!)]);
  if (!(e instanceof Float32Array) || !(sv instanceof Float32Array) || !(st instanceof Uint32Array)) throw new Error('the grid bundle’s arrays aren’t f32 fields, f32 scalp vertices and u32 scalp triangles');
  const field = createFieldSampler(sim.header, e);
  const coil = createCoilProjector(sim.header, sv, st, yRef);
  return { field, coil, magnitude: new Float32Array(field.vertexCount), pose: new Float32Array(16), vertexCount: field.vertexCount, patch: patchOf(sim.header) };
}

/** What the panel says of a basis: its poses, its patch, and the validated error of its live field, from the bundle itself:
 *  IXI165's STIM-008 result sits in the header's `validation`; Ernie's STIM-001 median is in its validation file
 *  (`basis.validation`, read when the panel opens the bundle). Null numbers where a bundle records none. */
export interface BasisFacts {
  poses: number;
  patch: Patch;
  medianError: number | null;
  maxError: number | null;
  study: string | null;
}
const finite = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
/** An error figure: a number, or (Ernie's STIM-001 file) one per method, the basis' own under `bilinear`. */
const error = (v: unknown): number | null => finite(v) ?? (v && typeof v === 'object' ? finite((v as Record<string, unknown>).bilinear) : null);
export function basisFacts(h: Pick<StimHeader, 'basis' | 'validation'>, file?: Record<string, unknown> | null): BasisFacts {
  const v = h.validation ?? {};
  const f = file ?? {};
  return {
    poses: h.basis?.pose_indices?.length ?? h.basis?.offsets_mm?.length ?? 0,
    patch: patchOf(h),
    medianError: error(v.median_vector_relative_l2) ?? error(f.median_vector_relative_l2),
    maxError: error(v.max_vector_relative_l2) ?? error(f.max_vector_relative_l2) ?? error(f.candidate_max_vector_relative_l2),
    study: typeof f.id === 'string' && /^STIM-\d+/.test(f.id) ? f.id : typeof v.summary === 'string' ? (/\b(STIM-\d+)\b/.exec(v.summary)?.[1] ?? null) : null,
  };
}

export interface LivePeak {
  value: number;
  vertex: number;
}

/** One step of the drag: the field at the offset and intensity and the coil's pose there, sampled into the basis' own
 *  buffers (vectors interpolated before the norm, no extrapolation: the offset is clamped to the patch first), then both
 *  pushed together, pose then field, without a timestamp, when the viewport has the methods. Returns the peak for the panel. */
export function liveUpdate(vp: Pick<LiveViewport, 'setVertexData' | 'setPose'>, live: LiveBasis, offset: Offset, k: number, drawn: CoilDrawn = { casing: false, dipoles: true }): LivePeak {
  const [x, y] = clampOffset(offset, live.patch);
  live.field.sample(x, y, live.magnitude, k);
  live.coil.pose(x, y, live.pose);
  if (typeof vp.setPose === 'function') {
    if (drawn.casing) vp.setPose(CASING_MESH, live.pose);
    if (drawn.dipoles) vp.setPose(COIL_MESH, live.pose);
  }
  if (typeof vp.setVertexData === 'function') vp.setVertexData(FIELD_LAYER, live.magnitude);
  const m = live.magnitude;
  let value = -Infinity;
  let vertex = -1;
  for (let i = 0; i < m.length; i++) if (m[i]! > value) [value, vertex] = [m[i]!, i];
  return { value, vertex };
}

/** The pose's centre in the head, mm, and its distance from the scalp, for the panel. */
export function poseFacts(pose: { matrix?: number[][]; didt_A_per_s?: number; scalp_distance_mm?: number } | null): { centre: [number, number, number] | null; didtMAs: number | null; gapMm: number | null } {
  if (!pose) return { centre: null, didtMAs: null, gapMm: null };
  const m = pose.matrix;
  return {
    centre: m ? [m[0]![3]!, m[1]![3]!, m[2]![3]!] : null,
    didtMAs: pose.didt_A_per_s !== undefined ? pose.didt_A_per_s / 1e6 : null,
    gapMm: pose.scalp_distance_mm ?? null,
  };
}
